import SelectField from '@maison/components/form/fields/SelectField';
import FormGroup from '@maison/components/form/FormGroup';
import PhoenixForm from '@maison/components/form/PhoenixForm';

import StoreResultsInModal from '@maison/components/store/StoreResultsInModal';
import { clientApi } from '@phoenix/clientApi';
import modalMixin from '@phoenix/mixins/modalMixin';
import { find, get, has, set } from 'lodash-es';

export default {
  components: { StoreResultsInModal, FormGroup, PhoenixForm, SelectField },

  mixins: [modalMixin],

  props: {
    countries: {
      type: Array,
      required: true,
    },
    modelReference: {
      type: String,
      default: '',
    },
    collectionTitle: {
      type: String,
      default: '',
    },
    productTitle: {
      type: String,
      default: '',
    },
    references: {
      type: Array,
      required: true,
    },
    noResultCTA: {
      type: Object,
      required: true,
    },
    productTracking: {
      type: Object,
      default: () => ({}),
    },
    fallbackReference: {
      type: String,
      default: '',
    },
    protoBoutiques: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      isLoading: false,
      isShown: false,
      stores: {},
      formSubmitted: false,
      isFallbackReference: {},
      shouldDisplayStock: {},
      fields: {
        reference: {
          id: 'reference',
          name: 'reference',
          label: this.$t('Product.selectYourSize'),
          value: null,
          options: [],
          required: true,
          type: 'select',
          forceSelect: true,
        },
        country: {
          id: 'country',
          name: 'country',
          label: this.$t('Form.label.country'),
          value: window.Phoenix.currentCountry,
          type: 'select',
          forceSelect: true,
          options: [],
          required: true,
        },
      },
    };
  },

  computed: {
    storesForCountry() {
      return get(this.stores, [this.fields.reference.value, this.fields.country.value], []);
    },

    isFallbackReferenceForCountry() {
      return get(this.isFallbackReference, [this.fields.reference.value, this.fields.country.value], false);
    },

    fallbackReferences() {
      return this.parseFallbackReferences(this.fallbackReference);
    },

    shouldDisplayStockForCountry() {
      return get(this.shouldDisplayStock, [this.fields.reference.value, this.fields.country.value], true);
    },

    shouldDisplaySizeSelector() {
      return this.fields.reference.options.length > 1;
    },

    modalTitle() {
      return this.$t('Product.boutiqueStockAvailability.title');
    },

    noResultTitle() {
      return this.$t('Product.boutiqueStockAvailability.noResultTitle');
    },

    noResultText() {
      return this.$t('Product.boutiqueStockAvailability.noResultText');
    },

    modalOptions() {
      return {
        onShow: async () => {
          this.isShown = true;
          await this.submit();
        },
        onHidden: () => (this.isShown = false),
      };
    },
  },

  watch: {
    countries: {
      handler(newValues) {
        this.fields.country.options = newValues;

        if (newValues.length > 0 && find(newValues, ['value', this.fields.country.value]) === undefined) {
          this.fields.country.value = newValues[0].value;
        }
      },
      immediate: true,
    },

    async 'fields.country.value'() {
      await this.submit();
    },

    async 'fields.reference.value'() {
      await this.submit();
    },
  },

  methods: {
    async submit() {
      if (!this.isShown) {
        return;
      }

      await this.fetchStores(this.fields.reference.value, this.fields.country.value);
      this.formSubmitted = true;
    },

    async fetchStores(reference, country) {
      if (has(this.stores, [reference, country])) {
        return;
      }

      const configuredBoutiques = get(this.protoBoutiques, country, []);
      const hasConfiguredBoutiques = configuredBoutiques.length > 0;
      const forcedStockBoutiques = configuredBoutiques.map((b) => ({ ...b, hasStock: true }));
      const shouldDisplayStock = !hasConfiguredBoutiques;

      let boutiques = [];
      let isFallbackReference = false;
      let shouldUpdateStores = false;

      this.isLoading = true;

      try {
        const response = await this.getBoutiqueStockAvailability(reference, country);

        if (hasConfiguredBoutiques) {
          const boutiqueIds = new Set(configuredBoutiques.map(({ id }) => id));

          boutiques = [
            ...forcedStockBoutiques,
            ...response.data.data.boutiques
              .filter(({ id }) => !boutiqueIds.has(id))
              .map((b) => ({ ...b, hasStock: false })),
          ];
        } else {
          boutiques = response.data.data.boutiques;
        }

        if (!hasConfiguredBoutiques && this.shouldUseFallbackReference(reference, boutiques)) {
          boutiques = await this.fetchStoresForFallbackReferences(reference, country);
          isFallbackReference = true;
        }

        shouldUpdateStores = true;
      } catch (error) {
        window.Phoenix.sentry.captureException(error);

        if (hasConfiguredBoutiques) {
          boutiques = forcedStockBoutiques;
          shouldUpdateStores = true;
        }
      } finally {
        if (shouldUpdateStores) {
          set(this.stores, [reference, country], boutiques);
          set(this.isFallbackReference, [reference, country], isFallbackReference);
          set(this.shouldDisplayStock, [reference, country], shouldDisplayStock);
        }

        this.isLoading = false;
      }
    },

    async fetchStoresForFallbackReferences(reference, country) {
      const fallbackReferences = this.eligibleFallbackReferences(reference);
      let firstFallbackStores = [];

      for (const [index, fallbackReference] of fallbackReferences.entries()) {
        const stores = await this.fetchStoresForReference(fallbackReference, country);
        const hasStock = this.hasStock(stores);

        if (hasStock) {
          return stores;
        }

        const isFirstFallbackReference = index === 0;

        if (isFirstFallbackReference) {
          firstFallbackStores = stores;
        }
      }

      return firstFallbackStores;
    },

    async fetchStoresForReference(reference, country) {
      if (!has(this.stores, [reference, country])) {
        const response = await this.getBoutiqueStockAvailability(reference, country);

        set(this.stores, [reference, country], response.data.data.boutiques);
      }

      return get(this.stores, [reference, country], []);
    },

    async getBoutiqueStockAvailability(reference, country) {
      return clientApi.get(window.route('products.boutiqueStockAvailability'), {
        params: { reference, country },
      });
    },

    storeClick(store) {
      window.tracking.pushEvent(window.tracking.getProductPageBsaodTracking(store.id, store.name));
      window.location.href = store.permalink;
    },

    parseFallbackReferences(fallbackReference) {
      return fallbackReference
        .split(',')
        .map((reference) => reference.trim())
        .filter((reference) => reference.length > 0);
    },

    eligibleFallbackReferences(reference) {
      return this.fallbackReferences.filter((fallbackReference) => fallbackReference !== reference);
    },

    hasStock(stores) {
      return stores.length > 0;
    },

    shouldUseFallbackReference(reference, stores) {
      return this.eligibleFallbackReferences(reference).length > 0 && !this.hasStock(stores);
    },
  },
};
